import styled from "@emotion/styled";
import * as React from "react";
import * as Ons from "react-onsenui";
import { default as ChatBubbleSvg } from "../../../../public/assets/chat-message-bubble.svg?react";
import useDisablePushNotifications from "../../../hooks/useDisablePushNotifications";
import { AgencyId, CommCenterTicket } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { AppButton } from "../../Generic/AppButton";
import { InfoCard } from "../../Generic/InfoCard";
import ChatsPreviewContainer from "./ChatsPreviewContainer";

type Props =
  | {
      isLoading: false;
      caregiverFirstName: string;
      agencyId: AgencyId;
      tickets: CommCenterTicket[];
      isCreatingNewTicket: boolean;
      canChatWithOtherAgencies: boolean;
      onClickChatWithOtherAgencies: () => void;
      onTicketClick: (ticket: CommCenterTicket) => void;
      onClickNewTicket: () => void;
    }
  | {
      isLoading: true;
      caregiverFirstName: string;
      onClickChatWithOtherAgencies: () => void;
    };

const ChatsMainScreen = (props: Props) => {
  useDisablePushNotifications({
    name: "ChatsMainScreen",
    disableIf: (notification) => {
      return notification?.type === "COMM_CENTER_TICKET_MESSAGE";
    },
  });

  return (
    <ChatsMainScreen.Root>
      <ChatsMainScreen.Wrapper>
        <ChatInfoCard>
          {`${props.caregiverFirstName}, ${translate(STRINGS.DO_YOU_HAVE_ANY_QUESTIONS_ASK)} `}
          <ChatsMainScreen.AgencyText>
            {translate(STRINGS.MEDFLYT_HOME_CARE)}
          </ChatsMainScreen.AgencyText>
          <ChatsMainScreen.ButtonWrapper>
            <AppButton
              size="small"
              variant="round"
              isLoading={props.isLoading ? false : props.isCreatingNewTicket}
              onClick={props.isLoading ? undefined : props.onClickNewTicket}
            >
              <ChatsMainScreen.ChatIconWrapper>
                <ChatBubbleSvg />
              </ChatsMainScreen.ChatIconWrapper>
              {translate(STRINGS.SEND_A_MESSAGE)}
            </AppButton>
          </ChatsMainScreen.ButtonWrapper>
        </ChatInfoCard>
        <ChatsMainScreen.Body>
          {props.isLoading || props.tickets.length > 0 ? (
            <ChatsMainScreen.PreviousTitle>
              {translate(STRINGS.PREVIOUS_CONVERSATIONS)}
            </ChatsMainScreen.PreviousTitle>
          ) : null}
          <ChatsMainScreen.ContentWrapper>
            {props.isLoading ? (
              <Ons.ProgressCircular indeterminate={true} />
            ) : (
              <ChatsPreviewContainer tickets={props.tickets} onTicketClick={props.onTicketClick} />
            )}
            {!props.isLoading && props.canChatWithOtherAgencies ? (
              <ChatsMainScreen.ChatWithOtherButtonWrapper>
                <ChatsMainScreen.ChatWithOtherButton
                  variant="filled"
                  onClick={props.onClickChatWithOtherAgencies}
                >
                  {translate(STRINGS.CHAT_WITH_OTHER_AGENCIES)}
                </ChatsMainScreen.ChatWithOtherButton>
              </ChatsMainScreen.ChatWithOtherButtonWrapper>
            ) : null}
          </ChatsMainScreen.ContentWrapper>
        </ChatsMainScreen.Body>
      </ChatsMainScreen.Wrapper>
    </ChatsMainScreen.Root>
  );
};

const ChatInfoCard = styled(InfoCard)`
  &.AppCard {
    background-color: rgba(224, 235, 252, 1);
  }
`;

ChatsMainScreen.ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  gap: 16px;
`;

ChatsMainScreen.Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
`;

ChatsMainScreen.Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
`;

ChatsMainScreen.Wrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  flex-grow: 1;
`;

ChatsMainScreen.ButtonWrapper = styled.div`
  margin-top: 16px;
  margin-left: 5px;
`;

ChatsMainScreen.AgencyText = styled.span`
  font-weight: 700;
`;

ChatsMainScreen.PreviousTitle = styled.div`
  font-weight: 500;
  font-size: 12px;
`;

ChatsMainScreen.AllConversationsTitle = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  font-weight: 600;
  font-size: 16px;
  color: var(--primary-color);
`;

ChatsMainScreen.ChatIconWrapper = styled.div`
  color: white;
  margin-inline-end: 5px;
`;

ChatsMainScreen.Content = styled.div``;

ChatsMainScreen.ChatWithOtherButtonWrapper = styled.div`
  position: sticky;
  bottom: 16px;
`;

ChatsMainScreen.ChatWithOtherButton = styled(AppButton)`
  margin-inline-end: 0;
  width: 100%;
`;

export default ChatsMainScreen;
